import { useState } from 'react'
import { Link } from 'react-router-dom'
import { RunWithSummary } from '../../types'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Calendar, Trash2, Edit, MapPin, Flag, Utensils, Route, Clock, Loader2 } from 'lucide-react'
import { formatDate } from '../../lib/calculations'

interface RunCardProps {
  run: RunWithSummary
  onDelete: () => void
  isDeleting?: boolean
}

export function RunCard({ run, onDelete, isDeleting }: RunCardProps) {
  const [isNavigating, setIsNavigating] = useState(false)

  const handleEditClick = (e: React.MouseEvent) => {
    if (isNavigating || isDeleting) {
      e.preventDefault()
      return
    }
    setIsNavigating(true)
  }
  // Calculate summary stats
  const totalDistance = run.segments?.reduce((sum, segment) => sum + segment.distance_km, 0) || 0
  const segmentCount = run.segments?.length || 0
  const aidStationCount = run.aid_stations?.length || 0
  const planItemCount = run.plan_items?.length || 0
  
  // Determine if run is "ready" (has segments and plan)
  const isPlanned = segmentCount > 0 && planItemCount > 0
  const isEmpty = segmentCount === 0 && aidStationCount === 0 && planItemCount === 0

  return (
    <div className="group relative">
      <Card className="bg-white border border-neutral-200 shadow-minimal hover:shadow-minimal-lg transition-all duration-300 hover:-translate-y-1 rounded-2xl overflow-hidden">
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0 space-y-3">
              <div>
                <CardTitle className="text-xl font-bold text-primary-900 line-clamp-2 group-hover:text-primary-700 transition-colors">
                  {run.title}
                </CardTitle>
                {run.date && (
                  <div className="flex items-center text-neutral-600 mt-2">
                    <Calendar className="h-4 w-4 mr-2 text-primary-600" />
                    <span className="font-medium">{formatDate(run.date)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              {isPlanned && (
                <Badge className="bg-emerald-600 text-white border-0 px-3 py-1">
                  ✓ Complete
                </Badge>
              )}
              {isEmpty && (
                <Badge variant="outline" className="text-primary-700 border-primary-300 bg-primary-50 px-3 py-1">
                  Draft
                </Badge>
              )}
              {!isEmpty && !isPlanned && (
                <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 px-3 py-1">
                  In Progress
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
      
        <CardContent className="pt-0 pb-6">
          {/* Statistics Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-xl border border-neutral-100">
              <div className="p-2 bg-primary-600 rounded-lg">
                <Route className="h-4 w-4 text-white" />
              </div>
              <div>
                <div className="text-xs font-medium text-primary-700 uppercase tracking-wide">Distance</div>
                <div className="font-bold text-primary-900 text-lg">
                  {totalDistance > 0 ? `${totalDistance.toFixed(1)}km` : '—'}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-xl border border-neutral-100">
              <div className="p-2 bg-violet-600 rounded-lg">
                <MapPin className="h-4 w-4 text-white" />
              </div>
              <div>
                <div className="text-xs font-medium text-violet-700 uppercase tracking-wide">Segments</div>
                <div className="font-bold text-primary-900 text-lg">{segmentCount}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-xl border border-neutral-100">
              <div className="p-2 bg-orange-600 rounded-lg">
                <Flag className="h-4 w-4 text-white" />
              </div>
              <div>
                <div className="text-xs font-medium text-orange-700 uppercase tracking-wide">Aid Stations</div>
                <div className="font-bold text-primary-900 text-lg">{aidStationCount}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-xl border border-neutral-100">
              <div className="p-2 bg-emerald-600 rounded-lg">
                <Utensils className="h-4 w-4 text-white" />
              </div>
              <div>
                <div className="text-xs font-medium text-emerald-700 uppercase tracking-wide">Nutrition</div>
                <div className="font-bold text-primary-900 text-lg">{planItemCount}</div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {run.notes && (
            <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-100">
              <p className="text-sm text-neutral-700 line-clamp-3 italic leading-relaxed">
                "{run.notes}"
              </p>
            </div>
          )}

          {/* Empty state message */}
          {isEmpty && (
            <div className="text-center py-6 bg-neutral-50 rounded-xl border border-dashed border-neutral-200">
              <Clock className="h-8 w-8 mx-auto mb-3 text-neutral-400" />
              <div className="font-medium text-neutral-700">Ready to plan your adventure</div>
              <div className="text-xs text-neutral-500 mt-1">Start by adding segments and planning your nutrition</div>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex justify-between items-center pt-4 gap-3 bg-neutral-50 border-t border-neutral-100">
          <Button
            asChild
            size="sm"
            className="flex-1 bg-primary-700 hover:bg-primary-800 text-white border-0 shadow-minimal hover:shadow-minimal-lg transition-all duration-200 font-medium"
            disabled={isNavigating || isDeleting}
          >
            <Link to={`/runs/${run.id}`} className="flex items-center justify-center" onClick={handleEditClick}>
              {isNavigating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Edit className="h-4 w-4 mr-2" />
              )}
              {isEmpty ? 'Start Planning' : 'Edit Plan'}
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            disabled={isDeleting || isNavigating}
            className="text-accent-600 hover:text-accent-700 hover:bg-accent-50 border-accent-200 bg-white shrink-0 hover:border-accent-300 transition-all"
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}